import React, { useEffect, useState } from 'react';
import { DollarSign, Percent, ShoppingBag, Network, TrendingUp, Loader2, Trophy } from 'lucide-react';
import { adminApi } from '../services/api';

const RevenueDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    adminApi.revenue()
      .then((r) => { if (alive) setData(r); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <div className="bg-white border border-slate-200 rounded-2xl p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="revenue-dashboard">
      <div className="flex items-center gap-2">
        <div className="h-8 w-1 rounded-full bg-emerald-500" />
        <h2 className="text-lg font-semibold text-slate-900">Revenue & commission</h2>
        <span className="text-xs text-slate-400 ml-1">Derived from paid Stripe transactions</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <RevTile icon={DollarSign} label="Commission MTD" value={fmt(data.mtd.commission)}
          sub={`All-time: ${fmt(data.all_time.commission)}`} tint="emerald" testid="rev-commission-mtd" />
        <RevTile icon={ShoppingBag} label="Gross MTD" value={fmt(data.mtd.gross)}
          sub={`${data.mtd.paid_orders} paid orders`} tint="slate" testid="rev-gross-mtd" />
        <RevTile icon={Network} label="Connect share" value={`${data.mtd.connect_share_pct}%`}
          sub="of paid orders this month" tint="indigo" testid="rev-connect-share" />
        <RevTile icon={Percent} label="All-time gross" value={fmt(data.all_time.gross)}
          sub="from paid payment_transactions" tint="amber" testid="rev-all-time-gross" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <section className="bg-white border border-slate-200 rounded-2xl p-5 lg:col-span-3">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
            <h3 className="font-semibold text-slate-900">Last 14 days</h3>
            <span className="text-xs text-slate-400 ml-1">Gross (bars) · Commission (line)</span>
          </div>
          <SeriesChart series={data.daily} />
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-amber-500" />
            <h3 className="font-semibold text-slate-900">Top vendors (30d, paid)</h3>
          </div>
          {(!data.top_vendors || data.top_vendors.length === 0) ? (
            <div className="text-sm text-slate-500 py-6 text-center">No paid transactions yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.top_vendors.map((v, idx) => (
                <div key={v.id} className="py-3 flex items-center gap-3" data-testid={`top-vendor-${idx}`}>
                  <div className="h-7 w-7 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center text-xs font-semibold">{idx + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{v.name}</div>
                    <div className="text-[11px] text-slate-500">{v.orders} paid · fee {fmt(v.fee)}</div>
                  </div>
                  <div className="font-semibold text-slate-900">{fmt(v.gross)}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const TINTS = {
  emerald: 'from-emerald-500/10 to-emerald-500/0 text-emerald-700 [&_svg]:text-emerald-600',
  slate: 'from-slate-500/10 to-slate-500/0 text-slate-700 [&_svg]:text-slate-600',
  indigo: 'from-indigo-500/10 to-indigo-500/0 text-indigo-700 [&_svg]:text-indigo-600',
  amber: 'from-amber-500/10 to-amber-500/0 text-amber-700 [&_svg]:text-amber-600',
};

const RevTile = ({ icon: Icon, label, value, sub, tint = 'emerald', testid }) => (
  <div className={`relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-5`} data-testid={testid}>
    <div className={`absolute -top-6 -right-6 h-24 w-24 rounded-full bg-gradient-to-br ${TINTS[tint]}`} />
    <div className="relative">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-3 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  </div>
);

const SeriesChart = ({ series }) => {
  if (!series || series.length === 0) return <div className="text-sm text-slate-500 py-6 text-center">No data yet.</div>;
  const maxGross = Math.max(1, ...series.map((s) => s.gross));
  const maxFee = Math.max(1, ...series.map((s) => s.fee));
  const w = 540, h = 160, pad = 20;
  const xStep = (w - pad * 2) / Math.max(1, series.length - 1);
  // Polyline for commission (fee)
  const pts = series.map((s, i) => `${pad + i * xStep},${h - pad - (s.fee / maxFee) * (h - pad * 2)}`).join(' ');
  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-44">
        {/* horizontal guides */}
        {[0.25, 0.5, 0.75].map((p, i) => (
          <line key={i} x1={pad} y1={pad + p * (h - pad * 2)} x2={w - pad} y2={pad + p * (h - pad * 2)} stroke="#e2e8f0" strokeDasharray="3 4" />
        ))}
        {/* bars */}
        {series.map((s, i) => {
          const bh = (s.gross / maxGross) * (h - pad * 2);
          const x = pad + i * xStep - 8;
          return <rect key={i} x={x} y={h - pad - bh} width={16} height={bh} rx={3} fill="#10b981" opacity={0.18} />;
        })}
        {/* line */}
        <polyline points={pts} fill="none" stroke="#0f172a" strokeWidth={2} />
        {series.map((s, i) => {
          const cy = h - pad - (s.fee / maxFee) * (h - pad * 2);
          const cx = pad + i * xStep;
          return <circle key={i} cx={cx} cy={cy} r={3} fill="#0f172a" />;
        })}
        {/* X axis labels (sparse) */}
        {series.map((s, i) => (i % 3 === 0) && (
          <text key={s.date} x={pad + i * xStep} y={h - 4} textAnchor="middle" fontSize="9" fill="#94a3b8">
            {s.date.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
};

export default RevenueDashboard;
